import { Loader } from "@/components/shared";
import { client, databases } from "@/lib/appwrite/config";
import { useGetCurrentUser, useLastMessages, useGetUserById } from "@/lib/react-query/queries";
import { IUser } from "@/types";
import { useEffect, useState, useMemo } from "react"; // Added useMemo
import ChatList from "./ChatList";
import MessageView from "./MessageView";
import { useNavigate, useParams, useSearchParams } from "react-router-dom"; // Added useSearchParams

interface LastMessagesMap {
  [userId: string]: {  // Changed to string keys
    message: string;
    timestamp: Date;
    isRead?: boolean;
  };
}

const ChatContainer = () => {
  const { data: currentUser } = useGetCurrentUser();
  const { id } = useParams();
  const [searchParams] = useSearchParams(); // Add this
  const conversationUser1 = searchParams.get('conversation'); // Add this
  const conversationUser2 = searchParams.get('with'); // Add this
  const { data: targetUser } = useGetUserById(id || "");
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
  const [lastMessagesMap, setLastMessagesMap] = useState<LastMessagesMap>({});
  const { lastMessages, isLoading, error } = useLastMessages(currentUser?.$id || null);
  const databaseId = import.meta.env.VITE_APPWRITE_DATABASE_ID;
  const collectionId = import.meta.env.VITE_APPWRITE_MESSAGE_COLLECTION_ID;
  const navigate = useNavigate();

  // Helper function to get user by ID (add this)
  const getUserById = async (userId: string) => {
    try {
      const user = await databases.getDocument(
        import.meta.env.VITE_APPWRITE_DATABASE_ID,
        import.meta.env.VITE_APPWRITE_USER_COLLECTION_ID,
        userId
      );
      return user;
    } catch (error) {
      console.error('Error fetching user by ID:', error);
      throw error;
    }
  };

   // Reset selected user when navigating to /chats
   useEffect(() => {
    if (!id && !conversationUser1 && !conversationUser2) { // Updated condition
      setSelectedUser(null);
    }
  }, [id, conversationUser1, conversationUser2]); // Updated dependencies

  // Updated auto-select logic to handle both regular users and conversation pairs
  useEffect(() => {
    const handleUserSelection = async () => {
      if (conversationUser1 && conversationUser2) {
        // This is a conversation pair for agent monitoring
        try {
          const [user1, user2] = await Promise.all([
            getUserById(conversationUser1),
            getUserById(conversationUser2)
          ]);
          
          const conversationPair: any = {
            id: `${conversationUser1}-${conversationUser2}`,
            $id: `${conversationUser1}-${conversationUser2}`,
            name: `${user1.name} ↔ ${user2.name}`,
            username: `${user1.username}_${user2.username}`,
            emailOrPhone: '', // Not applicable for conversation pairs
            bio: `Conversation between ${user1.name} and ${user2.name}`,
            imageUrl: '',
            acceptedSLA: true,
            isConversation: true,
            participants: [user1, user2]
          };
          setSelectedUser(conversationPair);
        } catch (error) {
          console.error('Error fetching conversation participants:', error);
          navigate('/chats');
        }
      } else if (id && targetUser) {
        // Regular user chat (your existing logic)
        try {
          setSelectedUser({
            id: targetUser.$id,
            name: targetUser.name,
            username: targetUser.username,
            emailOrPhone: targetUser.emailOrPhone,
            bio: targetUser.bio || '',
            imageUrl: targetUser.imageUrl || '',
            acceptedSLA: targetUser.acceptedSLA
          });
        } catch (error) {
          navigate('/chats');
        }
      }
    };

    handleUserSelection();
  }, [id, targetUser, conversationUser1, conversationUser2, navigate]); // Updated dependencies
  
  // Memoize the subscription channel to prevent unnecessary recreations
  const subscriptionChannel = useMemo(() => 
    [`databases.${databaseId}.collections.${collectionId}.documents`], 
    [databaseId, collectionId]
  );

  // Optimized last messages initialization using reduce
  useEffect(() => {
    if (!lastMessages || !currentUser) return;

    const messagesMap = lastMessages.reduce((acc, message) => {
      const otherUserId = message.recipient_id === currentUser.$id 
      ? message.sender_id 
      : message.recipient_id;
      
      const existingTime = acc[otherUserId]?.timestamp?.getTime() || 0;
      const currentTime = new Date(message.timestamp || message.$createdAt).getTime();
        
      if (!acc[otherUserId] || currentTime > existingTime) {
        acc[otherUserId] = {
          message: message.message,
          timestamp: new Date(message.timestamp || message.$createdAt),
          isRead: message.is_read
        };
      }
      return acc;
    }, {} as LastMessagesMap);

    setLastMessagesMap(messagesMap);
  }, [lastMessages, currentUser]);

  // Optimized real-time updates with batching and error handling
  useEffect(() => {
    if (!currentUser) return;

    const unsubscribe = client.subscribe(
      subscriptionChannel,
      async (response) => {
        if (response.events.includes(`databases.${import.meta.env.VITE_APPWRITE_DATABASE_ID}.collections.${
          import.meta.env.VITE_APPWRITE_MESSAGE_COLLECTION_ID
        }.documents.*.create`)) {
          
          try {
            const newMessage = await databases.getDocument(
              import.meta.env.VITE_APPWRITE_DATABASE_ID,
              import.meta.env.VITE_APPWRITE_MESSAGE_COLLECTION_ID,
              (response.payload as { $id: string }).$id
            );

            setLastMessagesMap(prev => {
              // Now using direct string IDs
              const otherUserId = newMessage.recipient_id === currentUser.$id 
                ? newMessage.sender_id 
                : newMessage.recipient_id;
  
              const existingTime = prev[otherUserId]?.timestamp?.getTime() || 0;
              const currentTime = new Date(newMessage.timestamp || newMessage.$createdAt).getTime();
  
              if (!prev[otherUserId] || currentTime > existingTime) {
                return {
                  ...prev,
                  [otherUserId]: {
                    message: newMessage.message,
                    timestamp: new Date(newMessage.timestamp || newMessage.$createdAt),
                    isRead: newMessage.is_read
                  }
                };
              }
              return prev;
            });
          } catch (error) {
            console.error("Error processing new message:", error);
          }
        }
      }
    );  

    return () => unsubscribe();
  }, [currentUser, subscriptionChannel]);

  if (!currentUser) return <Loader />;
  if (isLoading) return <Loader />;
  if (error) return <div>Error loading messages: {error.message}</div>;

  return (
    <div className="flex mb-4 h-screen bg-gray-900">
      {selectedUser ? (
        <MessageView 
          selectedUser={selectedUser} 
          currentUserId={currentUser.$id} 
          onBack={() => navigate('/chats')} 
        />
      ) : (
        <ChatList 
          onSelectUser={(user) => {
            // Updated navigation logic to handle conversation pairs
            if (user.isConversation && user.participants) {
              // For conversation pairs, use query params
              const participant1Id = user.participants[0].$id || user.participants[0].id;
              const participant2Id = user.participants[1].$id || user.participants[1].id;
              navigate(`/chats?conversation=${participant1Id}&with=${participant2Id}`);
            } else {
              // Regular user chat (your existing logic)
              navigate(`/chats/${user.id}`);
            }
          }} 
          lastMessages={lastMessagesMap} 
        />
      )}
    </div>
  );
};

export default ChatContainer;